import { Routes } from '@angular/router';
import { environment } from '@env/environment.local';
import type { EnvironmentName } from '@env/environment.model';
import { onboardingAccessGuard } from './features/access/onboarding-access.guard';
import { documentSubmissionLeaveGuard } from './features/document-submission/document-submission-page/document-submission-leave.guard';

const catalogEnvironments: readonly EnvironmentName[] = ['local', 'develop'];

const localOnlyRoutes: Routes = [
  {
    path: '_catalog',
    loadComponent: () => import('./features/catalog/catalog-page/catalog-page').then((m) => m.CatalogPage),
    title: 'Catálogo',
  },
  {
    path: 'template',
    loadComponent: () => import('./features/template/template-page/template-page').then((m) => m.TemplatePage),
    title: 'Template',
  },
];

export const routes: Routes = [
  // Entrada desde el mail: /onboarding/{token}. Todo el flujo cuelga del token, así un F5 en cualquier
  // paso lo revalida y vuelve a pedir el RFC. Para crear URLs del flujo: `onboardingRoute()`.
  // El padre no tiene componente: los hijos heredan `:token` en su `paramMap`.
  {
    path: 'onboarding/:token',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'rfc' },
      {
        path: 'rfc',
        canActivate: [onboardingAccessGuard()],
        // La salida de sesión vuelve acá con `?reason=` aunque el cliente ya esté en esta página: solo cambian los query
        // params, o ni eso. El guard tiene que volver a consultar el token igual.
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import('./features/rfc-verification/rfc-verification-page/rfc-verification-page').then(
            (m) => m.RfcVerificationPage,
          ),
        title: 'Validación',
      },
      {
        path: 'documents',
        canActivate: [onboardingAccessGuard({ requiresRfc: true })],
        canDeactivate: [documentSubmissionLeaveGuard],
        loadComponent: () =>
          import('./features/document-submission/document-submission-page/document-submission-page').then(
            (m) => m.DocumentSubmissionPage,
          ),
        title: 'Documentos',
      },

      // Páginas de estado: sin guard a propósito. Son el destino de sus redirects; con guard harían un loop.
      {
        path: 'completed',
        loadComponent: () =>
          import('./features/status-pages/completed-page/completed-page').then((m) => m.CompletedPage),
        title: 'Proceso completado',
      },
      {
        path: 'expired',
        loadComponent: () => import('./features/status-pages/expired-page/expired-page').then((m) => m.ExpiredPage),
        title: 'Enlace no válido',
      },
      // Se agotaron los intentos de RFC: la misma página de vencido, con el texto del bloqueo.
      {
        path: 'locked',
        loadComponent: () => import('./features/status-pages/expired-page/expired-page').then((m) => m.ExpiredPage),
        data: { rfcLocked: true },
        title: 'Acceso bloqueado',
      },
    ],
  },

  {
    path: 'not-found',
    loadComponent: () => import('./features/status-pages/not-found-page/not-found-page').then((m) => m.NotFoundPage),
    title: 'Página no encontrada',
  },

  // Catálogo de componentes: solo en local y develop. angular.json reemplaza environment.local por el
  // de cada ambiente, así que en qa, preprod y production esta ruta no existe.
  ...(catalogEnvironments.includes(environment.name) ? localOnlyRoutes : []),
  { path: '**', redirectTo: 'not-found' },
];
